import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { REVIEWER, SHRINK_NETWORK_URL, networkUrl } from "@/lib/site";
import { breadcrumbSchema, startHereWebPageSchema } from "@/lib/schema";
import PageShareBar from "@/components/PageShareBar";

const PATH = "/start-here";

interface StartCard {
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
  to?: string;
  href?: string;
}

// Card copy is verbatim from the spec. Em-dashes are converted to colons per
// the site voice rules. The two guide cards point to the real /resources/
// routes (the spec's /articles/ paths don't exist on this site).
const CARDS: StartCard[] = [
  {
    eyebrow: "Acute",
    title: "I had a panic attack.",
    body: "The acute panic cluster: racing heart, chest tightness, dizziness, the certainty that something is medically wrong. The Panic Map shows the cluster of experiences that travel with it.",
    cta: "Open the Panic Map",
    to: "/maps/panic",
  },
  {
    eyebrow: "Worry",
    title: "I can't stop worrying.",
    body: "The chronic worry pattern: what-if loops, rumination, the inability to put a concern down. The Overthinking Map shows the cluster, and the GAD pages cover the formal condition.",
    cta: "Open the Overthinking Map",
    to: "/maps/overthinking",
  },
  {
    eyebrow: "Social",
    title: "I'm avoiding things I used to do.",
    body: "Avoidance is the engine of social anxiety. The Social Anxiety Map shows how the cluster builds: anticipatory dread, in-the-moment self-consciousness, the long after-event replay.",
    cta: "Open the Social Anxiety Map",
    to: "/maps/social-anxiety",
  },
  {
    eyebrow: "Health",
    title: "I'm worried something is medically wrong.",
    body: "Health anxiety is its own cluster: body scanning, symptom checking, the reassurance loop. The Health Anxiety Map shows how the cluster operates and what changes it.",
    cta: "Open the Health Anxiety Map",
    to: "/maps/health-anxiety",
  },
  {
    eyebrow: "Sleep",
    title: "I can't sleep because of anxiety.",
    body: "The sleep-anxiety loop has its own structure: the 3 a.m. wake-up, the pre-sleep racing thoughts, the cortisol cycle. The Sleep & Anxiety Map shows the cluster and what helps.",
    cta: "Open the Sleep & Anxiety Map",
    to: "/maps/sleep-and-anxiety",
  },
  {
    eyebrow: "Help someone",
    title: "I want to help someone with anxiety.",
    body: "For partners, parents, friends, and anyone supporting someone with anxiety. What helps, what doesn't, and how to avoid accidentally making things worse.",
    cta: "Read the guide",
    to: "/resources/supporting-a-partner-with-anxiety",
  },
  {
    eyebrow: "Diagnosed",
    title: "I was just diagnosed with anxiety.",
    body: "What to expect in the first month of treatment, how to think about therapy and medication, and what tends to be different at the six-month mark.",
    cta: "Read what happens next",
    to: "/resources/just-diagnosed-with-anxiety",
  },
  {
    eyebrow: "Care",
    title: "I want professional care.",
    body: "The Get Help section covers state-by-state options. shrinkMD is the network's telepsychiatry option, available in multiple states with evaluation usually within a few days.",
    cta: "Find help in your state",
    to: "/resources/state-resources",
  },
  {
    eyebrow: "Clinical",
    title: "I want the clinical definitions.",
    body: "Shrinkopedia is the network's clinical encyclopedia. For diagnostic criteria, DSM-5-TR references, evidence-strength ratings, and the formal clinical view, Shrinkopedia is the right place.",
    cta: "Open Shrinkopedia",
    href: "https://shrinkopedia.com",
  },
];

function CardCta({ label }: { label: string }) {
  return (
    <span
      className="inline-block mt-4 text-xs font-semibold"
      style={{ color: "hsl(var(--brand-text))" }}
    >
      {label}{" "}
      <span className="link-arrow" aria-hidden="true">
        &rarr;
      </span>
    </span>
  );
}

function ExperienceCard({ card }: { card: StartCard }) {
  const inner = (
    <>
      <span
        className="block text-[0.7rem] font-semibold uppercase tracking-widest"
        style={{ color: "hsl(var(--brand-text))" }}
      >
        {card.eyebrow}
      </span>
      <h3
        className="font-serif text-lg text-foreground"
        style={{ marginTop: "0.5rem" }}
      >
        {card.title}
      </h3>
      <p
        className="mt-2 text-sm leading-relaxed"
        style={{ color: "hsl(var(--muted-foreground))" }}
      >
        {card.body}
      </p>
      <CardCta label={card.cta} />
    </>
  );
  const cls = "group card-lift block p-6 h-full hover:no-underline";
  const style = {
    background: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderTop: "3px solid hsl(var(--brand))",
    borderRadius: "var(--radius)",
  } as const;
  if (card.href) {
    return (
      <a
        href={card.href}
        target="_blank"
        rel="noopener noreferrer"
        className={cls}
        style={style}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link to={card.to as string} className={cls} style={style}>
      {inner}
    </Link>
  );
}

export default function StartHerePage() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Start Here", path: PATH },
  ];
  const ld: object[] = [breadcrumbSchema(crumbs), startHereWebPageSchema({ path: PATH })];

  return (
    <>
      <Seo
        title="Start Here: What Brought You Here? | AnxietyResource"
        description="AnxietyResource is the practical layer for anxiety in the Shrink Network. Tell us what's closest to your situation and we'll point you to the right place."
        path={PATH}
        jsonLd={ld}
      />
      <div className="mx-auto max-w-[1100px] px-6 py-10">
        <Breadcrumbs items={crumbs} />

        <header className="max-w-[760px]">
          <div
            className="text-xs uppercase tracking-widest mb-3"
            style={{ color: "hsl(var(--brand-text))" }}
          >
            Start here
          </div>
          <h1 style={{ marginTop: 0 }}>What brought you here?</h1>
          <p className="mt-4 text-lg leading-relaxed" style={{ color: "hsl(var(--muted-foreground))" }}>
            AnxietyResource is the practical layer for anxiety in the Shrink
            Network. Tell us what's closest to your situation and we'll point you
            to the right place.
          </p>
          <p className="mt-3 text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>
            Reviewed by {REVIEWER.name}
          </p>

          <div className="prose-editorial mt-8">
            <p>
              Anxiety doesn't show up the same way for everyone. For some people
              it's the acute moment, the panic attack on the freeway, the chest
              tightness in the meeting, the wave of dread for no obvious reason.
              For others it's the constant low hum, the worry that runs in the
              background, the muscle tension that never quite eases, the sleep
              that refuses to restore. For others it's narrower than that, a
              specific situation, a specific kind of social moment, a specific
              health worry that won't stop circling.
            </p>
            <p>
              The cards below route you to wherever your situation sits closest.
              If you're not sure, "I had a panic attack" and "I can't stop
              worrying" are the two most common starting points; either is a fine
              place to begin. Once you're inside, the Anxiety Maps connect the
              experiences and the Continue Learning blocks at the bottom of every
              entry route you onward.
            </p>
            <p>
              AnxietyResource is one of several sites in{" "}
              <a
                href={networkUrl(`${SHRINK_NETWORK_URL}/`, "start_here")}
                target="_blank"
                rel="noopener noreferrer"
              >
                the Shrink Network
              </a>
              . Shrinkopedia is the clinical encyclopedia. PsychiatryRx covers the
              medications. shrinkMD provides the care. We cover the practical
              layer: what anxiety actually looks like, what it feels like, and
              what to do about it day to day. The cards that point off-site go to
              wherever the question fits best.
            </p>
          </div>
        </header>

<PageShareBar />

        <section className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((card) => (
            <ExperienceCard key={card.title} card={card} />
          ))}
        </section>

        <Link
          to="/maps"
          className="block mt-5 p-6 card-lift hover:no-underline"
          style={{
            background: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            borderTop: "3px solid hsl(var(--brand))",
            borderRadius: "var(--radius)",
          }}
        >
          <span
            className="block text-[0.7rem] font-semibold uppercase tracking-widest"
            style={{ color: "hsl(var(--brand-text))" }}
          >
            Not sure
          </span>
          <h3 className="font-serif text-lg text-foreground" style={{ marginTop: "0.5rem" }}>
            Not sure where to start?
          </h3>
          <p className="mt-2 text-sm leading-relaxed" style={{ color: "hsl(var(--muted-foreground))" }}>
            If none of the cards above feels exactly right, the Anxiety Maps hub
            is a good second-best starting place. Nine visual cluster maps
            covering the major anxiety patterns.
          </p>
          <CardCta label="Open the Anxiety Maps" />
        </Link>

        <p
          className="mt-10 max-w-[760px] text-sm leading-relaxed"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          Every site in the network is independent. Each one is its own resource
          with its own purpose.{" "}
          <a
            href={networkUrl(`${SHRINK_NETWORK_URL}/`, "start_here")}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline"
            style={{ color: "inherit" }}
          >
            The Shrink Network
          </a>{" "}
          connects them.
        </p>
      </div>
    </>
  );
}
